import React, { Suspense, useState } from "react";
import { ToastTypes } from "../../../../contexts/toastr.context";
import { Button, Loader, TextInput } from "../../../../_components";
import DropDown from "../../../../_components/atoms/drop-down.component";
import TextButton from "../../../../_components/atoms/text-button";
import { InputSwitch } from "../../../../_components/form-controls/input-switch.component";
import { EmailService } from "../../../../_service";
const RichTextEditor = React.lazy(() => import("../../../../_components/form-controls/rich-text-editor.component"));

export const SEND_BULK_MAIL_PAGE_ROUTE = "/settings/bulkmail/send";

/**
 * @description - Send bulk mail component
 * @param {object} user - The auth user object
 * @param {object} community - The current community object
 * @param {Array<Object>} groups - List of groups
 * @param {Function} addToast - The add toast function
 * @param {Function} onCancel - Callback function to handle cancel button click
 */
export default function SendMailPage({
  user,
  community,
  groups,
  addToast,
  onCancel = () => {},
  mailToDisplay,
}) {
  let optionsList = [
    {
      value: "everyone",
      label: "Everyone",
      name: "Everyone",
    },
  ];

  if (Array.isArray(groups) && groups.length > 0) {
    groups.map(
      (group) =>
        (optionsList = [
          ...optionsList,
          { value: group.id, label: group.name, name: group.name },
        ])
    );
  }
  const [selectedOption, setSelectedOption] = useState(
    mailToDisplay && mailToDisplay.group
      ? {
          value: mailToDisplay.group,
          label: getGroupNameFromId(mailToDisplay.group),
        }
      : optionsList[0]
  );
  const [emailBody, setEmailBody] = useState("");
  const [subjectLine, setSubjectLine] = useState("");

  const [sendingMail, setIsSendingMail] = useState(false);
  const [switchEditor, setSwitchEditor] = useState(false);

  const readOnly = mailToDisplay !== undefined;

  React.useEffect(() => {
    setEmailBody(mailToDisplay?.emailBody ?? "");
    setSubjectLine(mailToDisplay?.subjectLine ?? "");
    setSelectedOption(
      mailToDisplay?.group
        ? {
            value: mailToDisplay.group,
            label: getGroupNameFromId(mailToDisplay.group),
          }
        : optionsList[0]
    );
    setEmailBody((mailToDisplay && mailToDisplay.subjectLine) ?? "");
  }, [mailToDisplay]);

  // Send mail to all the members of community or selected groups
  function handleSendMail() {
    const data = {
      groupId:
        selectedOption.value === "everyone" ? null : selectedOption.value,
      subjectLine: subjectLine,
      emailBody: emailBody,
    };
    if (
      !window.confirm(
        `Are you sure you want to send mail to ${selectedOption.name}`
      )
    ) {
      return;
    }
    setIsSendingMail(true);
    EmailService.sendBulkMail(user, community.id, data)
      .then((res) => {
        addToast("Mail sent successfully", "", ToastTypes.success);
      })
      .catch((error) => {
        if (error.response) {
          if (error.response.status == 400) {
            addToast(error.response.data.message, "", ToastTypes.danger);
          }
        } else {
          addToast("Error sending mail", "", ToastTypes.danger);
        }
      })
      .finally(() => {
        setIsSendingMail(false);
      });
  }

  function getGroupNameFromId(id) {
    let groupName = "";
    if (!id) {
      return groupName;
    }
    groups.forEach((group) => {
      if (group.id === id) {
        groupName = group.name;
      }
    });
    return groupName;
  }

  return (
    <div className="SendMailPage theme-bg-surface p-4 mt-0.5 flex flex-col lg:flex-row lg:space-x-4 space-y-4  lg:space-y-0">
      <div className="flex flex-col space-y-5 border theme-border-default p-4 rounded theme-bg-default w-full lg:w-1/2">
        <div className="">
          <h3 className="font-semibold ">To:</h3>
          <DropDown
            options={optionsList}
            className="w-full"
            value={selectedOption}
            onChange={(val) => {
              setSelectedOption(val);
            }}
          />
        </div>

        <div>
          <h3 className="font-semibold ">Subject</h3>
          <TextInput
            inputHolderClassName="rounded theme-bg-surface"
            onChange={(val) => {
              setSubjectLine(val);
            }}
            placeholder="Enter mail subject"
            value={subjectLine}
            defaultValue={subjectLine}
            validator={(value) => {
              if (value.length > 0) {
                if (value.length > 120) {
                  return "Subject should be less than 120 characters";
                }
              } else {
                return "Subject is required";
              }
            }}
          />
        </div>
        <div className="flex flex-col space-y-2">
          <div className="flex items-center justify-between border theme-border-default rounded-lg theme-bg-default p-3 w-full flex-grow focus:outline-none resize-none mb-1">
            Switch to rich text editor
            <InputSwitch
              selected={switchEditor}
              className="mx-2 transform duration-500 ease-in-out"
              onClick={(e) => {
                setSwitchEditor(!switchEditor);
              }}
              alt
            />
          </div>

          <h3 className="font-semibold ">Write Message</h3>
          {switchEditor ? (
            <div className="flex flex-col space-y-1">
              <Suspense fallback={<></>}>
                <RichTextEditor
                  className=" rounded theme-bg-surface outline-none"
                  setText={(e) => {
                    setEmailBody(e);
                  }}
                  text={emailBody}
                />
              </Suspense>
              <span className="theme-text-subtitle-2 text-xs">
                Double click or select text to access formatting options.
              </span>
            </div>
          ) : (
            <textarea
              type="text"
              value={emailBody}
              onChange={(e) => {
                setEmailBody(e.target.value);
                window.setTimeout(() => {
                  e.target.style.height = "auto";
                  e.target.style.height = e.target.scrollHeight + "px";
                }, 0);
              }}
              className="px-2 py-3 w-full flex-grow block border theme-border-default theme-bg-surface focus:outline-none"
              rows="9"
              placeholder={"Enter message here"}
            />
          )}
        </div>
        {!readOnly && (
          <div className="flex justify-between">
            <TextButton
              label="Cancel"
              onClick={onCancel}
              isLoading={sendingMail}
            />
            <Button
              label="Send"
              isLoading={sendingMail}
              onClick={(e) => {
                handleSendMail();
              }}
              disabled={
                subjectLine === "" ||
                emailBody === "" ||
                subjectLine.length > 120
              }
            />
          </div>
        )}
      </div>
      <EditorBasics />
    </div>
  );

  function EditorBasics() {
    return (
      <div className="w-full lg:w-1/2">
        <div className="border theme-border-default p-4 theme-bg-default rounded">
          <div className="font-semibold ">Editor Basics</div>
          <ul className="">
            <li className="theme-text-subtitle-2 text-xs pt-0.5">
              {"Use {name} for Name of user"}
            </li>
            <li className="theme-text-subtitle-2 text-xs pt-0.5">
              {"Use {email} for email of user"}
            </li>
            <li className="theme-text-subtitle-2 text-xs pt-0.5">
              {"Use {group_name} for group name"}
            </li>
            <li className="theme-text-subtitle-2 text-xs pt-0.5">
              {"Use {community_name} for community name"}
            </li>
            <li className="theme-text-subtitle-2 text-xs pt-0.5">
              {"Use {community_url} for community address"}
            </li>
            <li className="theme-text-subtitle-2 text-xs pt-0.5">
              {"Do not use any space between {}"}
            </li>
          </ul>
        </div>
      </div>
    );
  }
}
